import { useEffect, useState } from 'react';
import { API_URL } from '../../shared/constant';
import LoaderComponent from '../../shared/loader';
import axios from 'axios';
import { isPWA, isMobilePWA } from "../../utils/pwa";
import { isWebView } from "../../utils/webview";
import { toast } from "react-toastify";
import "../../styles/pwa-customers.css";

export default function SearchOutStanding() {

    const [isLoading, setLoading] = useState(false);
    const [payType, setPayType] = useState('All');
    const [accountData, setAccountData] = useState([]);
    const [outstandingData, setOutstandingData] = useState({
        total_paid: 0,
        total_received: 0,
        final_amount: 0,
    });
    const [searchValue, setSearchValue] = useState("");
    const [pwaMode, setPwaMode] = useState(false);
    const [mobilePWA, setMobilePWA] = useState(false);
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [refreshing, setRefreshing] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [webViewMode, setWebViewMode] = useState(false);

    useEffect(() => {
        getAccount('All');
    }, []);

    const getAccount = (party_type) => {
        setPayType(party_type)
        setLoading(true);
        axios.post(API_URL.OUTSTANDING, { party_type: party_type }).then(res => {
            setAccountData(res.data.data);
            setOutstandingData(res.data.outstanding);
            setLoading(false);
            setRefreshing(false);
        }, error => {
            setLoading(false);
            setRefreshing(false);
            toast.error("Something went wrong..", {
                autoClose: 3000,
            });
        })
    };

    // Handle online/offline status
    useEffect(() => {
        const handleOnline = () => {
            setIsOnline(true);
            if (pwaMode) {
                toast.success("Connection restored", { autoClose: 2000 });
                getAccount(payType); // Refresh data when back online
            }
        };
        
        const handleOffline = () => {
            setIsOnline(false);
            if (pwaMode) {
                toast.warning("You're offline. Some features may not work.", { autoClose: 3000 });
            }
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, [pwaMode, payType]);

    // Pull to refresh functionality
    useEffect(() => {
        if (!pwaMode || !mobilePWA) return;

        let startY = 0;
        let currentY = 0;
        let isPulling = false;
        let pullElement = null;

        const handleTouchStart = (e) => {
            if (window.scrollY === 0) {
                startY = e.touches[0].clientY;
                pullElement = document.querySelector('.pwa-outstanding-list');
            }
        };

        const handleTouchMove = (e) => {
            if (window.scrollY === 0 && pullElement) {
                currentY = e.touches[0].clientY;
                const pullDistance = currentY - startY;

                if (pullDistance > 50) {
                    isPulling = true;
                    pullElement.style.transform = `translateY(${Math.min(pullDistance * 0.3, 50)}px)`;
                    pullElement.style.opacity = '0.7';
                }
            }
        };

        const handleTouchEnd = () => {
            if (isPulling && pullElement) {
                pullElement.style.transform = '';
                pullElement.style.opacity = '';
                
                setRefreshing(true);
                getAccount(payType).finally(() => {
                    setRefreshing(false);
                    toast.success("Data refreshed", { autoClose: 1500 });
                });
                
                isPulling = false;
            }
        };

        document.addEventListener('touchstart', handleTouchStart, { passive: true });
        document.addEventListener('touchmove', handleTouchMove, { passive: true });
        document.addEventListener('touchend', handleTouchEnd, { passive: true });

        return () => {
            document.removeEventListener('touchstart', handleTouchStart);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleTouchEnd);
        };
    }, [pwaMode, mobilePWA, payType]);

    useEffect(() => {
        getAccount('All');
        // Check PWA mode, mobile, and webview
        setPwaMode(isPWA());
        setMobilePWA(isMobilePWA());
        setIsMobile(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
        setWebViewMode(isWebView());
    }, []);

    return (
        <div>
            {/* PWA Header - Only show in PWA mode or mobile (not webview) */}
            {(pwaMode || (isMobile && !webViewMode)) && (
                <div className="pwa-header bg-primary text-white p-3 mb-3">
                    <div className="d-flex align-items-center justify-content-between">
                        <h4 className="mb-0">
                            <i className="simple-icon-wallet me-2"></i>
                            Outstanding Payments ({accountData.length})
                        </h4>
                        <div className="d-flex align-items-center gap-2">
                            {!isOnline && (
                                <div className="pwa-status-indicator bg-warning">
                                    <i className="simple-icon-wifi me-1"></i>
                                    Offline
                                </div>
                            )}
                            {mobilePWA && (
                                <div className="pwa-status-indicator">
                                    <i className="simple-icon-screen-smartphone me-1"></i>
                                    PWA
                                </div>
                            )}
                            {refreshing && (
                                <div className="pwa-status-indicator bg-info">
                                    <i className="simple-icon-refresh me-1"></i>
                                    Refreshing
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            
            <div className={`${(pwaMode || (isMobile && !webViewMode)) ? 'pwa-content' : ''}`}>
                {(pwaMode || (isMobile && !webViewMode)) ? (
                    // PWA Mobile Card Layout
                    <div className="pwa-outstanding-list">
                        {pwaMode && mobilePWA && (
                            <div className="pwa-pull-hint text-center text-muted mb-3">
                                <small>
                                    <i className="simple-icon-arrow-down me-1"></i>
                                    Pull down to refresh
                                </small>
                            </div>
                        )}
                        
                        {/* PWA Filter Section */}
                        <div className="pwa-filter-section mb-3">
                            <div className="card">
                                <div className="card-body p-3">
                                    <div className="d-flex justify-content-center gap-3">
                                        <div className="form-check">
                                            <input 
                                                name="group1" 
                                                type="radio" 
                                                value={'All'} 
                                                checked={payType === "All"} 
                                                onChange={(e) => getAccount(e.target.value)} 
                                                id="pwa-radio-1" 
                                                className="form-check-input" 
                                            />
                                            <label htmlFor="pwa-radio-1" className="form-check-label">All</label>
                                        </div>
                                        <div className="form-check">
                                            <input 
                                                name="group1" 
                                                type="radio" 
                                                value={'Seller'} 
                                                checked={payType === "Seller"} 
                                                onChange={(e) => getAccount(e.target.value)} 
                                                id="pwa-radio-2" 
                                                className="form-check-input" 
                                            />
                                            <label htmlFor="pwa-radio-2" className="form-check-label">Sales</label>
                                        </div>
                                        <div className="form-check">
                                            <input 
                                                name="group1" 
                                                type="radio" 
                                                value={'Buyer'} 
                                                checked={payType === "Buyer"} 
                                                onChange={(e) => getAccount(e.target.value)} 
                                                id="pwa-radio-3" 
                                                className="form-check-input" 
                                            />
                                            <label htmlFor="pwa-radio-3" className="form-check-label">Purchase</label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* PWA Search Section */}
                        <div className="pwa-search-section mb-3">
                            <div className="input-group">
                                <input
                                    type="text"
                                    value={searchValue}
                                    onChange={(e) => setSearchValue(e.target.value)}
                                    className="form-control form-control-lg"
                                    placeholder="Search by party name..."
                                />
                                <button className="btn btn-outline-secondary" type="button">
                                    <i className="simple-icon-magnifier"></i>
                                </button>
                            </div>
                        </div>

                        {/* PWA Summary Card */}
                        <div className="pwa-summary-card mb-3">
                            <div className="card bg-light">
                                <div className="card-body p-3">
                                    <h6 className="card-title mb-3">Summary</h6>
                                    <div className="row text-center">
                                        <div className="col-4">
                                            <small className="text-muted d-block">Total Paid</small>
                                            <small className="fw-bold text-primary">₹{outstandingData.total_paid.toFixed(2)}</small>
                                        </div>
                                        <div className="col-4">
                                            <small className="text-muted d-block">Total Received</small>
                                            <small className="fw-bold text-success">₹{outstandingData.total_received.toFixed(2)}</small>
                                        </div>
                                        <div className="col-4">
                                            <small className="text-muted d-block">Final Amount</small>
                                            <small className={`fw-bold ${(outstandingData.final_amount < 0) ? 'text-success' : 'text-danger'}`}>
                                                ₹{(Math.abs(outstandingData.final_amount)).toFixed(2)} {(outstandingData.final_amount > 0) ? 'Dr' : 'Cr'}
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {isLoading && <LoaderComponent mode='full' height={100} width={100}></LoaderComponent>}
                        {!isLoading && (
                            <div>
                                {accountData.filter(acc => 
                                    !searchValue || 
                                    acc.party_name?.toLowerCase().includes(searchValue.toLowerCase())
                                ).map((acc, k) => {
                                    return (
                                        <div key={k} className="pwa-outstanding-card mb-3">
                                            <div className="card">
                                                <div className="card-body">
                                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                                        <div>
                                                            <h6 className="card-title mb-1">{acc.party_name}</h6>
                                                            <small className="text-muted">#{k + 1}</small>
                                                        </div>
                                                        <div className="pwa-status-badge">
                                                            <span className={`badge ${(acc.total < 0) ? 'bg-success' : 'bg-danger'}`}>
                                                                {(acc.total > 0) ? 'Dr' : 'Cr'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="pwa-outstanding-details mb-3">
                                                        <div className="row">
                                                            <div className="col-6">
                                                                <small className="text-muted d-block">Paid / Sales</small>
                                                                <small className="fw-bold text-primary">₹{Number(acc.paid || 0).toFixed(2)}</small>
                                                            </div>
                                                            <div className="col-6">
                                                                <small className="text-muted d-block">Received / Purchase</small>
                                                                <small className="fw-bold text-success">₹{Number(acc.received || 0).toFixed(2)}</small>
                                                            </div>
                                                        </div>
                                                        <div className="row mt-2">
                                                            <div className="col-12">
                                                                <small className="text-muted d-block">Total</small>
                                                                <small className={`fw-bold ${(acc.total < 0) ? 'text-success' : 'text-danger'}`}>
                                                                    ₹{Number(Math.abs(acc.total || 0)).toFixed(2)} {(acc.total > 0) ? 'Dr' : 'Cr'}
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                                
                                {accountData.filter(acc => 
                                    !searchValue || 
                                    acc.party_name?.toLowerCase().includes(searchValue.toLowerCase())
                                ).length === 0 && (
                                    <div className="text-center py-5">
                                        <i className="simple-icon-wallet" style={{fontSize: '3rem'}}></i>
                                        <h5>No outstanding payments found</h5>
                                        <p className="text-muted">No data available for the selected filter</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ) : (
                    // Original Desktop Table Layout
                    <div className='card'>
                        <div className='card-header'>
                            <span className='d-inline-block'>Outstanding</span>
                            {webViewMode && (
                                <small className="text-muted ms-2">
                                    <i className="simple-icon-screen-smartphone me-1"></i>
                                    WebView Mode
                                </small>
                            )}
                        </div>
                        <div className='card-body p-0'>
                            <div className="p-3 border-bottom text-center">
                                <div className="form-check form-check-inline">
                                    <input name="group1" type="radio" value={'All'} checked={payType === "All"} onChange={(e) => getAccount(e.target.value)} id="inline-radio-1" className="form-check-input" />
                                    <label title="All" htmlFor="inline-radio-1" className="form-check-label">All</label>
                                </div>
                                <div className="form-check form-check-inline">
                                    <input name="group1" type="radio" value={'Seller'} checked={payType === "Seller"} onChange={(e) => getAccount(e.target.value)} id="inline-radio-2" className="form-check-input" />
                                    <label title="Sales" htmlFor="inline-radio-2" className="form-check-label">Sales</label>
                                </div>
                                <div className="form-check form-check-inline">
                                    <input name="group1" type="radio" value={'Buyer'} checked={payType === "Buyer"} onChange={(e) => getAccount(e.target.value)} id="inline-radio-3" className="form-check-input" />
                                    <label title="Purchase" htmlFor="inline-radio-3" className="form-check-label">Purchase</label>
                                </div>
                            </div>
                            {
                                !isLoading && <div className="table-responsive">
                                    <table className='table table-hover m-0'>
                                        <thead className='thead'>
                                            <tr>
                                                <td>Party</td>
                                                <td>Paid / Sales</td>
                                                <td>Received / Purchase</td>
                                                <td>Total</td>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {accountData.map((acc, k) => {
                                                return <tr key={k}>
                                                    <td>{acc.party_name}</td>
                                                    <td>{Number(acc.paid || 0).toFixed(2)}</td>
                                                    <td>{Number(acc.received || 0).toFixed(2)}</td>
                                                    <td className={(acc.total < 0) ? 'text-success' : 'text-danger'}>
                                                        {Number(Math.abs(acc.total || 0)).toFixed(2)} {(acc.total > 0) ? 'Dr' : 'Cr'}
                                                    </td>

                                                </tr>
                                            })
                                            }
                                        </tbody>
                                        <thead className='thead'>
                                            <tr>
                                                <th>Total</th>
                                                <th>{outstandingData.total_paid.toFixed(2)}</th>
                                                <th>{outstandingData.total_received.toFixed(2)}</th>
                                                <td className={(outstandingData.final_amount < 0) ? 'text-success' : 'text-danger'}>{(Math.abs(outstandingData.final_amount)).toFixed(2)} {(outstandingData.final_amount > 0) ? 'Dr' : 'Cr'}</td>
                                            </tr>
                                        </thead>
                                    </table>
                                </div>
                            }
                        </div>
                    </div>
                )}
            </div>
            {
                isLoading && <LoaderComponent mode='full' height={100} width={100}></LoaderComponent>
            }
        </div>
    )
}